// Vanessa, 4 Nov 2026: comments and covering notes keep their shape instead
// of running into one block. A small, safe subset: blank lines make
// paragraphs, "- " or "* " lines make bullets, "1. " lines make a numbered
// list, **text** is bold. Built as React elements, never as raw HTML.
import { useRef } from "react";
import { Bold, List, ListOrdered } from "lucide-react";

// Vanessa, 4 Nov: links in comments are real, clickable links. https only.
const URL_RE = /(https:\/\/[^\s<>"')\]]+)/g;

function linked(text, keyBase) {
  return String(text).split(URL_RE).map((p, i) => {
    if (i % 2 === 1) {
      const href = p.replace(/[.,;:!?]+$/, "");
      const tail = p.slice(href.length);
      let label = href;
      try { const u = new URL(href); label = `${u.hostname}${u.pathname.length > 1 ? "/\u2026" : ""}`; } catch { /* keep the raw text */ }
      return (
        <span key={`${keyBase}-l${i}`}>
          <a href={href} target="_blank" rel="noopener noreferrer" title={href}
            className="text-indigo-300 underline hover:text-white break-all">{label}</a>{tail}
        </span>
      );
    }
    return <span key={`${keyBase}-t${i}`}>{p}</span>;
  });
}

function inline(text, keyBase) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**") && p.length > 4
    ? <strong key={`${keyBase}-${i}`}>{linked(p.slice(2, -2), `${keyBase}-${i}`)}</strong>
    : <span key={`${keyBase}-${i}`}>{linked(p, `${keyBase}-${i}`)}</span>));
}

const BULLET = /^\s*[-*•]\s+/;
const NUMBER = /^\s*\d+[.)]\s+/;

export function blocks(text) {
  const out = [];
  let para = [];
  const flush = () => { if (para.length) { out.push({ type: "p", lines: para }); para = []; } };
  for (const line of String(text || "").replace(/\r\n/g, "\n").split("\n")) {
    const kind = BULLET.test(line) ? "ul" : NUMBER.test(line) ? "ol" : null;
    if (kind) {
      flush();
      const item = line.replace(kind === "ul" ? BULLET : NUMBER, "");
      const last = out[out.length - 1];
      if (last && last.type === kind) last.items.push(item); else out.push({ type: kind, items: [item] });
    } else if (!line.trim()) {
      flush();
    } else {
      para.push(line);
    }
  }
  flush();
  return out;
}

export default function RichText({ text, className = "" }) {
  return (
    <div className={`space-y-1.5 break-words [overflow-wrap:anywhere] ${className}`} data-testid="rich-text">
      {blocks(text).map((b, i) => {
        if (b.type === "ul") return <ul key={i} className="list-disc pl-4 space-y-0.5">{b.items.map((t, j) => <li key={j}>{inline(t, `${i}-${j}`)}</li>)}</ul>;
        if (b.type === "ol") return <ol key={i} className="list-decimal pl-4 space-y-0.5">{b.items.map((t, j) => <li key={j}>{inline(t, `${i}-${j}`)}</li>)}</ol>;
        return <p key={i}>{b.lines.map((l, j) => <span key={j}>{j > 0 && <br />}{inline(l, `${i}-${j}`)}</span>)}</p>;
      })}
    </div>
  );
}

// A textarea with Bold, Bullets and Numbered buttons that insert the markup.
export function FormattedTextarea({ value, onChange, testId, ...rest }) {
  const ref = useRef(null);
  const apply = (kind) => {
    const el = ref.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    let next;
    if (kind === "bold") {
      const sel = value.slice(start, end) || "bold text";
      next = `${value.slice(0, start)}**${sel}**${value.slice(end)}`;
    } else {
      const lineStart = value.lastIndexOf("\n", start - 1) + 1;
      const chunk = value.slice(lineStart, end) || "";
      const lines = (chunk || "").split("\n");
      const marked = lines.map((l, i) => (kind === "ul" ? `- ${l.replace(BULLET, "")}` : `${i + 1}. ${l.replace(NUMBER, "")}`)).join("\n");
      next = `${value.slice(0, lineStart)}${marked}${value.slice(end)}`;
    }
    onChange(next);
    if (el) setTimeout(() => el.focus(), 0);
  };
  const btn = "p-1 rounded hover:bg-white/10 text-slate-400 hover:text-white";
  return (
    <div>
      <div className="flex gap-1 mb-1">
        <button type="button" className={btn} title="Bold" onClick={() => apply("bold")} data-testid={`${testId}-bold`}><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" className={btn} title="Bullets" onClick={() => apply("ul")} data-testid={`${testId}-bullets`}><List className="w-3.5 h-3.5" /></button>
        <button type="button" className={btn} title="Numbered list" onClick={() => apply("ol")} data-testid={`${testId}-numbered`}><ListOrdered className="w-3.5 h-3.5" /></button>
      </div>
      <textarea ref={ref} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} {...rest} />
    </div>
  );
}
